<template>
  <div v-bind:id="id" class="member-user-sort-rule-selector">
    <b-form-select
      v-bind:id="elementId.field"
      size="sm"
      v-bind:options="fieldOptions"
      v-model="localSortRule.field"
      class="field-selector"
      v-on:change="$_emitChangedEvent"
    />
    <b-form-select
      v-bind:id="elementId.direction"
      size="sm"
      v-bind:options="directionOptions"
      v-model="localSortRule.isAsc"
      class="order-selector"
      v-on:change="$_emitChangedEvent"
    />
  </div>
</template>

<script>
import {
  USER_STATUS_SORT_FIELD,
  getDefaultSortRule,
} from '@/composables/member/UseUserStatusSortRule'
import { ITEM_TYPE } from '@/composables/member/UseUserStatusAdditionalItems'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { mapGetters } from 'vuex'

// ソート可能なユーザー属性タイプ
const SORTABLE_ATTRIBUTE_TYPES = [
  ATTRIBUTE_TYPE.STRING,
  ATTRIBUTE_TYPE.NUMBER,
  ATTRIBUTE_TYPE.DATE,
  ATTRIBUTE_TYPE.DATETIME,
  ATTRIBUTE_TYPE.SINGLE_SELECT,
]

// 日時系のユーザー属性タイプ
const DATELIKE_ATTRIBUTE_TYPES = [ATTRIBUTE_TYPE.DATE, ATTRIBUTE_TYPE.DATETIME]

export default {
  name: 'AppMemberUserSortRuleSelector',
  props: {
    /**
     * ルート要素のID
     * 子要素のIDのprefixとしても使用
     */
    id: {
      type: String,
      default: 'user-sort-rule-selector',
    },
    /**
     * ソートルール
     */
    sortRule: {
      type: Object,
      default: () => ({ field: '', isAsc: true }),
    },
    /**
     * 絞り込み条件
     */
    condition: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    return { handleApiError }
  },
  data: function () {
    return {
      // ソートルール(内部管理用)
      localSortRule: {
        field: '',
        isAsc: true,
      },
    }
  },
  computed: {
    ...mapGetters('userAttribute', ['userAttributes', 'userAttributeMap']),
    ...mapGetters('sensorItem', ['sensorItems']),
    /**
     * 要素ID
     * @type {Object}
     */
    elementId: function () {
      return {
        field: `${this.id}-field`,
        direction: `${this.id}-direction`,
      }
    },
    /**
     * プレイスでソート可能な場合はtrue
     * @type {Boolean}
     */
    canSortByPlace: function () {
      return !!this.condition.place?.placeId
    },
    /**
     * アクティビティ(停止状態)でソート可能な場合はtrue
     * @type {Boolean}
     */
    canSortByActivity: function () {
      return !!this.condition.activity?.isStill
    },
    /**
     * 表示項目マスタ(system_setting user_disp_item)の値
     * @type {String|undefined} 未取得の場合は undefined
     */
    userDispItem: function () {
      return this.$store.getters['systemSetting/getItemValueByItemKey'](
        'user_disp_item'
      )
    },
    /**
     * 登録日時でソート可能な場合はtrue
     * * ソートAPIが create_at に対応しているかを直接知る手段が無いため、
     *   表示項目マスタへの登録有無をもって代替判定する
     * @type {Boolean}
     */
    canSortByRegistration: function () {
      return (this.userDispItem?.split('|') || []).some(
        (item) => item.split(':')[0] === ITEM_TYPE.REGISTRATION
      )
    },
    /**
     * プレイス選択肢
     * @type {Array.<Object>}
     */
    placeOptions: function () {
      if (!this.canSortByPlace) return []
      return [
        {
          value: USER_STATUS_SORT_FIELD.PLACE,
          text: this.$t('appMemberUserSortRuleSelector.field.place'),
        },
      ]
    },
    /**
     * アクティビティ選択肢
     * @type {Array.<Object>}
     */
    activityOptions: function () {
      if (!this.canSortByActivity) return []
      return [
        {
          value: USER_STATUS_SORT_FIELD.ACTIVITY,
          text: this.$t('appMemberUserSortRuleSelector.field.activity'),
        },
      ]
    },
    /**
     * 登録日時選択肢
     * @type {Array.<Object>}
     */
    registrationOptions: function () {
      if (!this.canSortByRegistration) return []
      return [
        {
          value: USER_STATUS_SORT_FIELD.REGISTRATION,
          text: this.$t('appMemberUserSortRuleSelector.field.registration'),
        },
      ]
    },
    /**
     * 選択可能なユーザー属性選択肢
     * @type {Array.<Object>}
     */
    attributeOptions: function () {
      return this.userAttributes
        .filter((attr) =>
          SORTABLE_ATTRIBUTE_TYPES.includes(+attr.attribute_type)
        )
        .map((attr) => ({
          value: `${USER_STATUS_SORT_FIELD.ATTRIBUTE}:${attr.id}`,
          text: attr.attribute_name,
        }))
    },
    /**
     * 選択可能なセンサー測定項目選択肢
     * @type {Array.<Object>}
     */
    sensorOptions: function () {
      return this.sensorItems
        .filter((item) => !item.value_type)
        .map((item) => ({
          value: `${USER_STATUS_SORT_FIELD.SENSOR}:${item.id}`,
          text: item.sensor_item_name,
        }))
    },
    /**
     * ソート対象の選択肢
     * @type {Array.<Object>}
     */
    fieldOptions: function () {
      return [
        {
          value: USER_STATUS_SORT_FIELD.POSITIONING,
          text: this.$t('appMemberUserSortRuleSelector.field.positioning'),
        },
        ...this.registrationOptions,
        {
          value: USER_STATUS_SORT_FIELD.USER_NAME,
          text: this.$t('appMemberUserSortRuleSelector.field.userName'),
        },
        ...this.placeOptions,
        ...this.activityOptions,
        {
          value: USER_STATUS_SORT_FIELD.BATTERY,
          text: this.$t('appMemberUserSortRuleSelector.field.battery'),
        },
        ...this.sensorOptions,
        ...this.attributeOptions,
      ]
    },
    /**
     * ソート対象フィールドが日時系のユーザー属性の場合はtrue
     * @type {boolean}
     */
    isSortFieldDateLikeAttribute: function () {
      const [field, id] = this.localSortRule.field.split(':')
      if (field !== USER_STATUS_SORT_FIELD.ATTRIBUTE) return false

      const attribute = this.userAttributeMap.get(+id)
      return DATELIKE_ATTRIBUTE_TYPES.includes(
        +(attribute?.attribute_type || 0)
      )
    },
    /**
     * ソート方向の選択肢
     * @type {Array.<Object>}
     */
    directionOptions: function () {
      const field = this.localSortRule.field.split(':')[0]
      if (
        field === USER_STATUS_SORT_FIELD.POSITIONING ||
        field === USER_STATUS_SORT_FIELD.REGISTRATION
      ) {
        return [
          {
            value: false,
            text: this.$t('appMemberUserSortRuleSelector.direction.newer'),
          },
          {
            value: true,
            text: this.$t('appMemberUserSortRuleSelector.direction.older'),
          },
        ]
      }
      if (field === USER_STATUS_SORT_FIELD.PLACE) {
        return [
          {
            value: true,
            text: this.$t('appMemberUserSortRuleSelector.direction.nearer'),
          },
          {
            value: false,
            text: this.$t('appMemberUserSortRuleSelector.direction.farther'),
          },
        ]
      }
      if (field === USER_STATUS_SORT_FIELD.ACTIVITY) {
        return [
          {
            value: false,
            text: this.$t('appMemberUserSortRuleSelector.direction.shorter'),
          },
          {
            value: true,
            text: this.$t('appMemberUserSortRuleSelector.direction.longer'),
          },
        ]
      }
      // ユーザー属性は日時系の属性のみ文言を変える
      if (
        field === USER_STATUS_SORT_FIELD.ATTRIBUTE &&
        this.isSortFieldDateLikeAttribute
      ) {
        return [
          {
            value: false,
            text: this.$t('appMemberUserSortRuleSelector.direction.newer'),
          },
          {
            value: true,
            text: this.$t('appMemberUserSortRuleSelector.direction.older'),
          },
        ]
      }

      return [
        {
          value: true,
          text: this.$t('appMemberUserSortRuleSelector.direction.asc'),
        },
        {
          value: false,
          text: this.$t('appMemberUserSortRuleSelector.direction.desc'),
        },
      ]
    },
  },
  watch: {
    sortRule: {
      immediate: true,
      deep: true,
      handler: function (value) {
        this.localSortRule = { ...value }
      },
    },
    // ソート対象が変わったら方向は先頭の選択肢のものに変更する
    'localSortRule.field': function () {
      this.localSortRule.isAsc = this.directionOptions[0].value
    },
    canSortByPlace: function (value) {
      // プレイスでソート不可なのにプレイスを選択している場合、初期値に戻す
      if (!value && this.localSortRule.field === USER_STATUS_SORT_FIELD.PLACE) {
        this.localSortRule = getDefaultSortRule()
      }
    },
    canSortByActivity: function (value) {
      // 稼働状態でソート不可なのに稼働状態を選択している場合、初期値に戻す
      if (
        !value &&
        this.localSortRule.field === USER_STATUS_SORT_FIELD.ACTIVITY
      ) {
        this.localSortRule = getDefaultSortRule()
      }
    },
    // 表示項目マスタの取得後、登録日時でソート不可なのに登録日時を選択している場合、初期値に戻す
    // * ソートルールは sessionStorage に保持されるため、リロードでは元に戻らない
    // * 親へ通知しないと取得APIには古いソート条件が渡り続けるため、変更イベントも発行する
    userDispItem: {
      immediate: true,
      handler: function (value) {
        // マスタ未取得の間は判定しない
        if (value === undefined) return
        if (
          this.canSortByRegistration ||
          this.localSortRule.field !== USER_STATUS_SORT_FIELD.REGISTRATION
        ) {
          return
        }
        this.localSortRule = getDefaultSortRule()
        this.$_emitChangedEvent()
      },
    },
  },
  created: function () {
    this.$_getSensorItems()
    this.$_getUserAttributes()
  },
  methods: {
    /**
     * センサー項目データ取得
     */
    $_getSensorItems: async function () {
      try {
        await this.$store.dispatch('sensorItem/updateSensorItemsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch sensorItems. use fetched before.')
        )
      }
    },

    /**
     * ユーザー属性データ取得
     */
    $_getUserAttributes: async function () {
      try {
        await this.$store.dispatch(
          'userAttribute/updateUserAttributesWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch userAttributes. use fetched before.')
        )
      }
    },

    /**
     * ソートルール変更イベントを発行する
     */
    $_emitChangedEvent: function () {
      /**
       * ソートルール変更イベント
       * @param {Object} sortRule 変更後ソートルール
       */
      this.$emit('sort-rule-changed', this.localSortRule)
    },
  },
}
</script>

<style lang="scss" scoped>
.member-user-sort-rule-selector {
  display: grid;
  grid-template-columns: auto 3fr;
  align-items: center;
  column-gap: 6px;

  ::v-deep .custom-select {
    height: $button-size; // ボタンと同じ高さにする
  }
}
</style>
